import { Button, Snackbar, Alert } from "@mui/material";
import { ReactKeycloakProvider, useKeycloak } from "@react-keycloak/web";
import Keycloak from "keycloak-js";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const KEYCLOAK_URL = window.keycloakConfig?.url || "";
const KEYCLOAK_CLIENT_ID = window.keycloakConfig?.clientId || "";
const KEYCLOAK_REALM = window.keycloakConfig?.realm || "";
const KEYCLOAK_IDP_ALIAS = window.keycloakConfig?.idpAlias || "";

export function KeycloackButton() {
  const { t } = useTranslation();
  let keycloakContext: any;
  try {
    keycloakContext = useKeycloak();
  } catch (e) {
    keycloakContext = null;
  }
  const [error, setError] = useState<string | null>(null);

  const onClick = () => {
    if (
      !KEYCLOAK_URL ||
      !KEYCLOAK_REALM ||
      !KEYCLOAK_CLIENT_ID ||
      !keycloakContext?.keycloak?.login
    ) {
      setError(t("auth.noSsoConfigured"));
      return;
    }

    try {
      keycloakContext.keycloak.login({
        idpHint: KEYCLOAK_IDP_ALIAS,
      });
    } catch (e) {
      console.error("SSO Login error:", e);
      setError(t("auth.noSsoConfigured"));
    }
  };

  return (
    <>
      <Button fullWidth variant="contained" color="primary" onClick={onClick}>
        {window?.keycloakConfig?.buttonTitle || t("auth.ssoLogin")}
      </Button>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}

const KeycloackButtonProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const keycloakInitOptions = {
    onLoad: "check-sso",
    silentCheckSsoRedirectUri:
      window.location.origin + "/sso/silent-check-sso.html",
    pkceMethod: "S256",
    silentCheckSsoFallback: false,
  };

  const keycloak = useMemo(() => {
    const ssoAllowed = Boolean(window?.keycloakConfig?.url);
    if (!ssoAllowed) return null;
    try {
      return new Keycloak({
        url: KEYCLOAK_URL,
        realm: KEYCLOAK_REALM,
        clientId: KEYCLOAK_CLIENT_ID,
      });
    } catch (e) {
      console.error("Keycloak initialization error:", e);
      return null;
    }
  }, []);

  return keycloak ? (
    <ReactKeycloakProvider
      authClient={keycloak}
      initOptions={keycloakInitOptions}
    >
      {children}
    </ReactKeycloakProvider>
  ) : (
    <>{children}</>
  );
};

export default KeycloackButtonProvider;
